import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { driverAPI, deliveryAPI, restaurantAPI, paymentAPI } from '../api/api';
import { useAuth } from '../App';
import { Link } from 'react-router-dom';
import DriverStatusBadge, { DeliveryStatusBadge } from '../components/DriverStatusBadge';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState('overview');
  const [drivers, setDrivers] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveEvents, setLiveEvents] = useState([]);
  const [chargingId, setChargingId] = useState(null);

  async function handleCharge(deliveryId, e) {
    e.stopPropagation();
    setChargingId(deliveryId);
    try {
      await paymentAPI.chargeDelivery(deliveryId);
      setDeliveries((prev) =>
        prev.map((d) =>
          d._id === deliveryId ? { ...d, payment: { ...(d.payment || {}), status: 'charged' } } : d
        )
      );
    } catch (err) {
      alert(err.response?.data?.error || 'Charge failed');
    } finally {
      setChargingId(null);
    }
  }

  const socketRef = useRef(null);

  useEffect(() => {
    async function load() {
      try {
        const [dRes, delRes, rRes] = await Promise.all([
          driverAPI.getAll({ limit: 50 }),
          deliveryAPI.getAll({ limit: 30 }),
          restaurantAPI.getAll({ limit: 30 }),
        ]);
        setDrivers(dRes.data.drivers || []);
        setDeliveries(delRes.data.deliveries || []);
        setRestaurants(rRes.data.restaurants || []);
      } catch (err) {
        console.error('Admin load error:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Real-time socket
  useEffect(() => {
    socketRef.current = io(SOCKET_URL, { transports: ['websocket', 'polling'] });

    socketRef.current.on('connect', () => {
      socketRef.current.emit('join:admin');
    });

    const addEvent = (msg) => {
      setLiveEvents((prev) => [
        { msg, time: new Date().toLocaleTimeString(), id: Date.now() },
        ...prev.slice(0, 19),
      ]);
    };

    socketRef.current.on('driver:location', ({ driverId, name }) => {
      addEvent(`📍 ${name || driverId} updated location`);
      // Update driver in list
      setDrivers((prev) =>
        prev.map((d) => (d._id === driverId ? { ...d, _locationUpdated: Date.now() } : d))
      );
    });

    socketRef.current.on('driver:statusChange', ({ driverId, status }) => {
      addEvent(`🔄 Driver status → ${status}`);
      setDrivers((prev) =>
        prev.map((d) => (d._id === driverId ? { ...d, status } : d))
      );
    });

    socketRef.current.on('job:assigned', ({ delivery }) => {
      addEvent(`🚗 Job assigned: ${delivery?.dropoff?.address}`);
      setDeliveries((prev) => [delivery, ...prev.filter((d) => d._id !== delivery._id)]);
    });

    socketRef.current.on('job:delivered', ({ deliveryId }) => {
      addEvent(`✅ Delivery completed: ${deliveryId}`);
      setDeliveries((prev) =>
        prev.map((d) => (d._id === deliveryId ? { ...d, status: 'delivered' } : d))
      );
    });

    return () => socketRef.current?.disconnect();
  }, []);

  const stats = {
    totalDrivers: drivers.length,
    activeDrivers: drivers.filter((d) => d.status !== 'offline').length,
    availableDrivers: drivers.filter((d) => d.status === 'available').length,
    activeDeliveries: deliveries.filter((d) =>
      ['assigned', 'accepted', 'picked_up'].includes(d.status)
    ).length,
    completedToday: deliveries.filter((d) => {
      if (d.status !== 'delivered') return false;
      const today = new Date();
      const created = new Date(d.createdAt);
      return created.toDateString() === today.toDateString();
    }).length,
    totalRestaurants: restaurants.length,
  };

  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'drivers', label: `Drivers (${drivers.length})` },
    { key: 'deliveries', label: `Deliveries (${deliveries.length})` },
    { key: 'restaurants', label: `Restaurants (${restaurants.length})` },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-gray-800 px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1
            className="text-2xl font-black text-yellow-400"
            style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}
          >
            STARFUEL
          </h1>
          <span className="text-xs bg-yellow-400/20 text-yellow-400 px-2 py-0.5 rounded-full font-medium">
            ADMIN
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/finance" className="px-3 py-1.5 bg-yellow-400 text-black text-xs font-bold rounded-lg hover:bg-yellow-300 transition">📊 Financials</Link>
          <button
            onClick={() => { logout(); navigate('/login'); }}
          className="text-gray-400 hover:text-white text-sm transition"
        >
          Sign Out
          </button>
        </div>
      </header>

      <main className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        {/* Stat bar */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'Total Drivers', value: stats.totalDrivers, color: 'text-white' },
            { label: 'Active Now', value: stats.activeDrivers, color: 'text-green-400' },
            { label: 'Available', value: stats.availableDrivers, color: 'text-green-400' },
            { label: 'Live Deliveries', value: stats.activeDeliveries, color: 'text-yellow-400' },
            { label: 'Completed Today', value: stats.completedToday, color: 'text-blue-400' },
            { label: 'Restaurants', value: stats.totalRestaurants, color: 'text-purple-400' },
          ].map((s) => (
            <div key={s.label} className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-6">
          {/* Main panel */}
          <div className="flex-1 min-w-0">
            {/* Tabs */}
            <div className="flex gap-1 bg-gray-900 border border-gray-800 rounded-xl p-1 mb-5 overflow-x-auto">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    tab === t.key
                      ? 'bg-yellow-400 text-black'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Overview */}
            {tab === 'overview' && (
              <div className="space-y-4">
                <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
                  <h3 className="font-semibold mb-4">Active Deliveries</h3>
                  {deliveries.filter((d) =>
                    ['assigned', 'accepted', 'picked_up'].includes(d.status)
                  ).length === 0 ? (
                    <p className="text-gray-500 text-sm">No active deliveries right now</p>
                  ) : (
                    <div className="space-y-3">
                      {deliveries
                        .filter((d) => ['assigned', 'accepted', 'picked_up'].includes(d.status))
                        .map((d) => (
                          <div key={d._id} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0">
                            <div>
                              <p className="text-sm text-white">{d.dropoff?.address}</p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {d.driver?.name ? `Driver: ${d.driver.name}` : 'Unassigned'} •{' '}
                                {d.restaurant?.name}
                              </p>
                            </div>
                            <DeliveryStatusBadge status={d.status} />
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
                  <h3 className="font-semibold mb-4">Driver Fleet Status</h3>
                  <div className="space-y-2">
                    {drivers.slice(0, 8).map((d) => (
                      <div key={d._id} className="flex items-center justify-between">
                        <span className="text-sm text-white">{d.name}</span>
                        <DriverStatusBadge status={d.status} size="sm" />
                      </div>
                    ))}
                    {drivers.length === 0 && (
                      <p className="text-gray-500 text-sm">No drivers registered yet</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Drivers tab */}
            {tab === 'drivers' && (
              <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left px-4 py-3 text-gray-400 font-medium">Driver</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium hidden md:table-cell">Phone</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium">Status</th>
                      <th className="text-right px-4 py-3 text-gray-400 font-medium hidden md:table-cell">Earnings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drivers.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                          No drivers registered
                        </td>
                      </tr>
                    ) : (
                      drivers.map((d, i) => (
                        <tr key={d._id} className={i > 0 ? 'border-t border-gray-800' : ''}>
                          <td className="px-4 py-3 text-white font-medium">{d.name}</td>
                          <td className="px-4 py-3 text-gray-400 hidden md:table-cell">{d.phone}</td>
                          <td className="px-4 py-3">
                            <DriverStatusBadge status={d.status} size="sm" />
                          </td>
                          <td className="px-4 py-3 text-right text-yellow-400 hidden md:table-cell">
                            ${(d.computedEarnings ?? 0).toFixed(2)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Deliveries tab */}
            {tab === 'deliveries' && (
              <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left px-4 py-3 text-gray-400 font-medium">Dropoff</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium hidden md:table-cell">Restaurant</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium">Status</th>
                      <th className="text-right px-4 py-3 text-gray-400 font-medium">Fee</th>
                      <th className="text-right px-4 py-3 text-gray-400 font-medium">Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveries.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                          No deliveries yet
                        </td>
                      </tr>
                    ) : (
                      deliveries.map((d, i) => (
                        <tr
                          key={d._id}
                          onClick={() => navigate(`/delivery/${d._id}`)}
                          className={`cursor-pointer hover:bg-gray-800/50 transition-colors ${i > 0 ? 'border-t border-gray-800' : ''}`}
                        >
                          <td className="px-4 py-3 text-white max-w-xs truncate">{d.dropoff?.address}</td>
                          <td className="px-4 py-3 text-gray-400 hidden md:table-cell">{d.restaurant?.name}</td>
                          <td className="px-4 py-3">
                            <DeliveryStatusBadge status={d.status} />
                          </td>
                          <td className="px-4 py-3 text-right text-yellow-400">${d.fee}</td>
                          <td className="px-4 py-3 text-right">
                            {d.payment?.status === 'charged' ? (
                              <span className="text-green-400 text-xs font-semibold">Charged ✓</span>
                            ) : d.status === 'delivered' ? (
                              <button
                                onClick={(e) => handleCharge(d._id, e)}
                                disabled={chargingId === d._id}
                                className="px-3 py-1.5 bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-bold rounded-lg transition disabled:opacity-50"
                              >
                                {chargingId === d._id ? 'Charging…' : `Charge $${d.fee}`}
                              </button>
                            ) : (
                              <span className="text-gray-600 text-xs">—</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Restaurants tab */}
            {tab === 'restaurants' && (
              <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left px-4 py-3 text-gray-400 font-medium">Restaurant</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium hidden md:table-cell">Owner</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium">Plan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {restaurants.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                          No restaurants yet
                        </td>
                      </tr>
                    ) : (
                      restaurants.map((r, i) => (
                        <tr key={r._id} className={i > 0 ? 'border-t border-gray-800' : ''}>
                          <td className="px-4 py-3 text-white font-medium">{r.name}</td>
                          <td className="px-4 py-3 text-gray-400 hidden md:table-cell">
                            {r.owner?.email}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                              r.activePlan === 'enterprise' ? 'bg-green-400/20 text-green-400' :
                              r.activePlan === 'business' ? 'bg-yellow-400/20 text-yellow-400' :
                              'bg-gray-700 text-gray-400'
                            }`}>
                              {r.activePlan || 'starter'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Live events sidebar */}
          <div className="hidden lg:block w-72 shrink-0">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4 sticky top-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <h3 className="font-semibold text-sm">Live Events</h3>
              </div>
              {liveEvents.length === 0 ? (
                <p className="text-xs text-gray-600">Waiting for activity...</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {liveEvents.map((e) => (
                    <div key={e.id} className="text-xs border-b border-gray-800 pb-2">
                      <p className="text-gray-300">{e.msg}</p>
                      <p className="text-gray-600 mt-0.5">{e.time}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
